/*
 * Product card sibling check (Productive #132 / #358 / #210).
 *
 * Liquid product cards (snippets/product-card-colour-swatches.liquid) build colour
 * swatches from the siblings metafield, or `x-sibling-handle:` tags when it is absent.
 * Liquid cannot tell which siblings are online without all_products (20-lookup cap), so
 * each non-active swatch carries data-sibling-verify="<handle>" and this script removes
 * the ones the storefront cannot see, via window.theme.storefrontVisibility
 * (assets/storefront-product-visibility.js). Fails open: an unanswered handle keeps
 * its swatch.
 *
 * The swatch cap is applied after the check: Liquid renders swatches past the cap
 * `hidden` and the row carries data-swatch-max, so an online colourway beyond the cap
 * fills the place of an offline one instead of the row under-reporting the range. The
 * row itself is never removed (its height is reserved in component-card.css) so a
 * removal cannot shift the grid.
 *
 * Cards added later (carousels, recommendations, quick add) are picked up through a
 * MutationObserver.
 */
(() => {
	"use strict";

	const SELECTOR = "[data-sibling-verify]";
	let scheduled = false;

	function visibility() {
		return window.theme && window.theme.storefrontVisibility;
	}

	/* Show the first `max` remaining swatches. Stops at a hidden swatch still awaiting
	   its answer, so an unverified colourway is never revealed. */
	function recap(row) {
		const max = Number(row.dataset.swatchMax);
		if (!max) return;
		let shown = 0;
		for (const swatch of row.children) {
			if (shown >= max) {
				swatch.hidden = true;
			} else if (swatch.hidden && swatch.hasAttribute("data-sibling-verify")) {
				break;
			} else {
				swatch.hidden = false;
				shown += 1;
			}
		}
	}

	function apply() {
		const check = visibility();
		const rows = new Set();
		document.querySelectorAll(SELECTOR).forEach((swatch) => {
			const handle = swatch.getAttribute("data-sibling-verify");
			if (check.isOffline(handle)) {
				if (swatch.parentNode) rows.add(swatch.parentNode);
				swatch.remove();
			} else if (check.isOnline(handle)) {
				swatch.removeAttribute("data-sibling-verify");
				if (swatch.parentNode) rows.add(swatch.parentNode);
			}
		});
		rows.forEach(recap);
	}

	function run() {
		scheduled = false;
		const check = visibility();
		if (!check) return;

		apply();
		const handles = [...document.querySelectorAll(SELECTOR)].map((swatch) =>
			swatch.getAttribute("data-sibling-verify")
		);
		if (handles.length) check.verify(handles).then(apply);
	}

	function schedule() {
		if (scheduled) return;
		scheduled = true;
		window.setTimeout(run, 50);
	}

	function init() {
		run();
		if (typeof window.MutationObserver !== "function") return;
		new MutationObserver((mutations) => {
			const added = mutations.some((mutation) =>
				[...mutation.addedNodes].some(
					(node) =>
						node.nodeType === 1 && (node.matches(SELECTOR) || node.querySelector(SELECTOR))
				)
			);
			if (added) schedule();
		}).observe(document.body, { childList: true, subtree: true });
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
